// src/components/LegalUpdateModal.jsx
// LOOP LEGAL MOBILE SCROLL V21 — legal update modal uses 100dvh internal scroll + sticky accept actions.
// LOOP LEGAL & PAYMENTS PHASE 2
// Prompts existing logged-in users to accept the current Terms, Privacy and Payments Policy.

import React, { useEffect, useState } from "react";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db as defaultDb } from "../firebase";
import fsClient from "../data/firestoreClient";
import { isIosCapacitor } from "../utils/nativeHttp";
import { getManualAuth, isManualAuthLikelyValid } from "../utils/manualAuth";
import { LOOP_LEGAL_TERMS_VERSION, LOOP_LEGAL_PRIVACY_VERSION, LOOP_LEGAL_PAYMENTS_VERSION, LOOP_LEGAL_SNAPSHOT_VERSION, LOOP_LEGAL_UPDATED_LABEL, buildLoopLegalAcceptancePatch, hasAcceptedCurrentLoopLegal, loopLegalHistoryDocId } from "../utils/legalVersions";

const BRAND = "#00675f";

function getEffectiveLegalUser(user) {
  if (user && user.uid) return { uid: user.uid, email: user.email || "", displayName: user.displayName || "", source: "firebase_auth" };
  try {
    const ma = getManualAuth();
    if (ma && isManualAuthLikelyValid(ma) && ma.uid) return { uid: ma.uid, email: ma.email || "", displayName: ma.displayName || ma.name || "", source: "manual_ios_auth" };
  } catch (_) {}
  return null;
}

function legalPlatformLabel() {
  try {
    if (isIosCapacitor()) return "ios_capacitor";
    if (typeof navigator !== "undefined" && /android/i.test(navigator.userAgent || "")) return "android_or_mobile_web";
    return "web";
  } catch (_) { return "unknown"; }
}

function shouldSkipLegalModalForPath(pathname = "") {
  const path = String(pathname || "").toLowerCase();
  return ["/login","/register","/signup","/enter-code","/verify-email","/forgot-password","/reset-password","/terms","/terminos","/terminos-y-condiciones","/privacy","/privacy-policy","/privacidad","/politica","/politica-de-privacidad","/payments","/payments-policy","/pagos","/politica-de-pagos","/pagos-cancelaciones-reembolsos","/support","/soporte"].some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

function LegalLink({ href, children }) {
  return <a href={href} target="_blank" rel="noreferrer">{children}</a>;
}

export default function LegalUpdateModal({ db = defaultDb, user = null, userInfo = {}, isLoggedIn = false }) {
  const effectiveUser = getEffectiveLegalUser(user);
  const [checking, setChecking] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [accepted, setAccepted] = useState(false);

  // LOOP LEGAL MOBILE SCROLL V21
  // Mobile web/Safari can lock the body while a fixed overlay is open.
  // Mark the document so the modal owns vertical touch scrolling instead of
  // depending on the page behind it.
  useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;
    const root = document.documentElement;
    const body = document.body;
    try {
      root?.classList?.add("loop-legal-modal-open-v21");
      body?.classList?.add("loop-legal-modal-open-v21");
    } catch (_) {}
    return () => {
      try {
        root?.classList?.remove("loop-legal-modal-open-v21");
        body?.classList?.remove("loop-legal-modal-open-v21");
      } catch (_) {}
    };
  }, [open]);

  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;
    let retryIndex = 0;
    const retryDelays = [2500, 8000];

    async function checkLegalStatus({ isRetry = false } = {}) {
      if (!isRetry) {
        setError("");
        setAccepted(false);
      }

      setChecking(true);
      setOpen(false);

      try {
        if (!isLoggedIn || !effectiveUser?.uid) return;

        if (
          typeof window !== "undefined" &&
          shouldSkipLegalModalForPath(window.location?.pathname || "")
        ) {
          return;
        }

        let profile = null;
        if (isIosCapacitor()) {
          profile = await fsClient.getDocByPath(`users/${effectiveUser.uid}`, { db });
        } else {
          const snap = await getDoc(doc(db, "users", effectiveUser.uid));
          profile = snap.exists() ? snap.data() : null;
        }

        if (!cancelled) {
          setOpen(!hasAcceptedCurrentLoopLegal(profile || {}));
        }
      } catch (err) {
        if (!cancelled) {
          // Unknown legal state is NOT the same as "not accepted".
          // Keep the modal closed on transient Auth/Firestore/network failures and retry.
          setOpen(false);

          try {
            console.warn(
              "[LegalUpdateModal] status check failed; keeping modal closed until verification succeeds",
              err
            );
          } catch (_) {}

          if (retryIndex < retryDelays.length) {
            const delay = retryDelays[retryIndex++];
            retryTimer = setTimeout(() => {
              retryTimer = null;
              checkLegalStatus({ isRetry: true });
            }, delay);
          }
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    }

    checkLegalStatus();

    return () => {
      cancelled = true;
      if (retryTimer) {
        try { clearTimeout(retryTimer); } catch (_) {}
      }
    };
  }, [db, effectiveUser?.uid, isLoggedIn]);

  const handleAccept = async () => {
    if (!effectiveUser?.uid || saving) return;
    if (!accepted) { setError("Marca la casilla para confirmar que aceptas la versión actual."); return; }
    setSaving(true); setError("");
    try {
      const email = effectiveUser.email || userInfo?.email || "";
      const patch = buildLoopLegalAcceptancePatch({ uid: effectiveUser.uid, email, source: "legal_update_modal", from: effectiveUser.source || "app_session", platform: legalPlatformLabel() });
      const historyId = loopLegalHistoryDocId(effectiveUser.uid);
      const history = { uid: effectiveUser.uid, email: String(email || "").trim().toLowerCase() || null, accepted: true, acceptedAt: patch.legalAcceptedAt, acceptedFrom: patch.legalAcceptedFrom, acceptedSource: patch.legalAcceptedSource, termsVersion: LOOP_LEGAL_TERMS_VERSION, privacyVersion: LOOP_LEGAL_PRIVACY_VERSION, paymentsVersion: LOOP_LEGAL_PAYMENTS_VERSION, snapshotVersion: LOOP_LEGAL_SNAPSHOT_VERSION, minimumAgeConfirmed: true, platform: legalPlatformLabel(), userAgent: typeof navigator !== "undefined" ? String(navigator.userAgent || "").slice(0, 500) : null, createdAt: patch.legalAcceptedAt, updatedAt: patch.legalAcceptedAt };
      if (isIosCapacitor()) {
        await fsClient.setDocByPath(`users/${effectiveUser.uid}`, patch, { db });
        await fsClient.setDocByPath(`users/${effectiveUser.uid}/legalAcceptances/${historyId}`, history, { db, merge: true });
      } else {
        await setDoc(doc(db, "users", effectiveUser.uid), { ...patch, legalServerUpdatedAt: serverTimestamp() }, { merge: true });
        await setDoc(doc(db, "users", effectiveUser.uid, "legalAcceptances", historyId), { ...history, createdAt: serverTimestamp(), updatedAt: serverTimestamp() }, { merge: true });
      }
      setOpen(false);
    } catch (err) {
      console.error("[LegalUpdateModal] accept failed", err);
      setError("No pudimos guardar tu aceptación. Revisa tu conexión e intenta nuevamente.");
    } finally { setSaving(false); }
  };

  if (checking || !open) return null;
  return (
    <div className="loop-legal-modal-layer" role="presentation">
      <style>{styles}</style>
      <section className="loop-legal-modal" role="dialog" aria-modal="true" aria-labelledby="loop-legal-modal-title">
        <div className="loop-legal-modal-mark" aria-hidden="true">∞</div>
        <div className="loop-legal-modal-copy">
          <span>Actualización legal</span>
          <h2 id="loop-legal-modal-title">Acepta las condiciones actuales de Loop</h2>
          <p>Actualizamos términos, privacidad y pagos para cubrir reservas, negocios, suscripciones, comisiones, pagos online, cancelaciones, no-show, reembolsos, membresías y fidelidad.</p>
        </div>
        <div className="loop-legal-modal-version-grid" aria-label="Versiones legales actuales">
          <div><strong>Términos</strong><span>{LOOP_LEGAL_TERMS_VERSION}</span></div>
          <div><strong>Privacidad</strong><span>{LOOP_LEGAL_PRIVACY_VERSION}</span></div>
          <div><strong>Pagos</strong><span>{LOOP_LEGAL_PAYMENTS_VERSION}</span></div>
        </div>
        <div className="loop-legal-modal-links"><LegalLink href="/terms">Términos</LegalLink><LegalLink href="/privacy">Privacidad</LegalLink><LegalLink href="/payments">Pagos y reembolsos</LegalLink></div>
        <label className={`loop-legal-modal-check ${error && !accepted ? "is-error" : ""}`}><input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} /><span>Confirmo que he leído y acepto la versión {LOOP_LEGAL_UPDATED_LABEL} de los documentos legales de Loop Reservas.</span></label>
        {error ? <div className="loop-legal-modal-error">{error}</div> : null}
        <div className="loop-legal-modal-actions">
          <button type="button" className="loop-legal-modal-primary" disabled={saving} onClick={handleAccept}>{saving ? "Guardando..." : "Aceptar y continuar"}</button>
          <p className="loop-legal-modal-footnote">Para continuar usando Loop Reservas necesitamos registrar esta aceptación en tu perfil.</p>
        </div>
      </section>
    </div>
  );
}

const styles = `
html.loop-legal-modal-open-v21,
body.loop-legal-modal-open-v21{overscroll-behavior:contain!important;touch-action:pan-y!important}
.loop-legal-modal-layer{position:fixed;inset:0;z-index:999999;display:flex;align-items:flex-start;justify-content:center;box-sizing:border-box;width:100vw;min-height:100vh;min-height:100dvh;max-height:100dvh;padding:max(14px,env(safe-area-inset-top,0px)) 18px max(18px,env(safe-area-inset-bottom,0px));background:rgba(15,23,42,.38);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);overflow-y:auto!important;overflow-x:hidden!important;-webkit-overflow-scrolling:touch!important;overscroll-behavior:contain!important;touch-action:pan-y!important}
.loop-legal-modal{width:min(520px,100%);max-height:calc(100dvh - max(28px,env(safe-area-inset-top,0px) + env(safe-area-inset-bottom,0px) + 24px));overflow-y:auto!important;overflow-x:hidden!important;-webkit-overflow-scrolling:touch!important;overscroll-behavior:contain!important;touch-action:pan-y!important;box-sizing:border-box;border:1px solid rgba(207,225,222,.95);border-radius:30px;background:rgba(255,255,255,.98);box-shadow:0 34px 100px rgba(15,23,42,.22);padding:24px;color:#0f172a;margin:auto 0;display:flex;flex-direction:column;min-height:0}
.loop-legal-modal *{box-sizing:border-box}.loop-legal-modal-mark{width:46px;height:46px;display:grid;place-items:center;border-radius:17px;background:#f0fdfa;color:${BRAND};font-size:28px;font-weight:950;margin-bottom:16px;flex:0 0 auto}.loop-legal-modal-copy span{display:block;color:${BRAND};font-size:11px;font-weight:950;letter-spacing:.16em;text-transform:uppercase}.loop-legal-modal-copy h2{margin:7px 0 9px;font-size:clamp(28px,5vw,38px);line-height:.98;letter-spacing:-.065em;font-weight:950}.loop-legal-modal-copy p{margin:0;color:#64748b;font-size:14.5px;line-height:1.58;font-weight:700}.loop-legal-modal-version-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;margin-top:18px}.loop-legal-modal-version-grid div{border:1px solid #e2e8f0;border-radius:16px;background:#f8fafc;padding:11px;min-width:0}.loop-legal-modal-version-grid strong,.loop-legal-modal-version-grid span{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.loop-legal-modal-version-grid strong{font-size:11px;color:#0f172a;font-weight:950}.loop-legal-modal-version-grid span{margin-top:4px;color:#64748b;font-size:10px;font-weight:800}.loop-legal-modal-links{display:flex;flex-wrap:wrap;gap:8px;margin:16px 0}.loop-legal-modal-links a{border:1px solid rgba(0,103,95,.16);border-radius:999px;background:#f0fdfa;color:${BRAND};padding:8px 11px;text-decoration:none;font-size:12px;font-weight:950}.loop-legal-modal-check{display:grid;grid-template-columns:auto 1fr;gap:10px;align-items:start;border:1px solid #dbe5ee;border-radius:18px;background:#fff;padding:14px;color:#334155;font-size:13px;line-height:1.45;font-weight:800}.loop-legal-modal-check.is-error{border-color:#fecaca;background:#fff7f7}.loop-legal-modal-check input{margin-top:2px;accent-color:${BRAND}}.loop-legal-modal-error{margin-top:10px;border-radius:14px;background:#fff7f7;border:1px solid #fecaca;color:#b91c1c;padding:10px 12px;font-size:12px;font-weight:850}.loop-legal-modal-actions{position:sticky;bottom:-24px;z-index:2;margin:14px -24px -24px;padding:12px 24px calc(14px + env(safe-area-inset-bottom,0px));background:linear-gradient(180deg,rgba(255,255,255,.72),rgba(255,255,255,.98) 26%,#fff 100%);border-top:1px solid rgba(226,232,240,.75)}.loop-legal-modal-primary{width:100%;min-height:48px;border:0;border-radius:18px;background:${BRAND};color:#fff;font-size:14px;font-weight:950;cursor:pointer;box-shadow:0 18px 34px rgba(0,103,95,.2)}.loop-legal-modal-primary:disabled{opacity:.58;cursor:not-allowed}.loop-legal-modal-footnote{margin:10px 3px 0;color:#94a3b8;text-align:center;font-size:11.5px;line-height:1.4;font-weight:700}
@supports not (height:100dvh){.loop-legal-modal-layer{max-height:100vh}.loop-legal-modal{max-height:calc(100vh - max(28px,env(safe-area-inset-top,0px) + env(safe-area-inset-bottom,0px) + 24px))}}
@media(max-width:560px){.loop-legal-modal-layer{padding:max(10px,env(safe-area-inset-top,0px)) 12px max(12px,env(safe-area-inset-bottom,0px));align-items:flex-start}.loop-legal-modal{width:100%;border-radius:24px;padding:18px;max-height:calc(100dvh - max(22px,env(safe-area-inset-top,0px) + env(safe-area-inset-bottom,0px) + 18px));margin:auto 0}.loop-legal-modal-mark{width:40px;height:40px;border-radius:15px;font-size:24px;margin-bottom:12px}.loop-legal-modal-copy h2{font-size:clamp(25px,8vw,32px)}.loop-legal-modal-copy p{font-size:13.5px;line-height:1.48}.loop-legal-modal-version-grid{grid-template-columns:repeat(3,minmax(0,1fr));gap:6px;margin-top:14px}.loop-legal-modal-version-grid div{padding:9px 7px;border-radius:14px}.loop-legal-modal-version-grid strong{font-size:9.5px}.loop-legal-modal-version-grid span{font-size:8.8px}.loop-legal-modal-links{gap:6px;margin:12px 0}.loop-legal-modal-links a{padding:7px 9px;font-size:11px}.loop-legal-modal-check{padding:12px;border-radius:16px;font-size:12.5px}.loop-legal-modal-actions{bottom:-18px;margin:12px -18px -18px;padding:10px 18px calc(12px + env(safe-area-inset-bottom,0px))}.loop-legal-modal-primary{min-height:48px}}
@media(max-height:640px){.loop-legal-modal-copy p{font-size:13px;line-height:1.42}.loop-legal-modal-version-grid{margin-top:12px}.loop-legal-modal-links{margin:10px 0}.loop-legal-modal-mark{display:none}.loop-legal-modal-actions{padding-top:9px}}
`;
